import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Button, Dialog, DialogActions, DialogContent, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { usePathname, useRouter } from 'next/navigation';
import React from 'react';
import { useLocationStore } from '@src/store/modules/location.store';
interface TimerExpiredModalProps {
  open: boolean;
  onClose: () => void; // Keeping this for API compatibility, but will not use it for closing
}

/**
 * Modal displayed when the countdown timer expires
 * Informs the user that their selected timeslot is no longer available
 * and they need to select a new one
 */
// Custom styled button to match the Results page style
const FindAppointmentButton = styled(Button)(() => ({
  backgroundColor: '#003087',
  color: '#FFFFFF',
  fontWeight: 600,
  padding: '10px 20px',
  fontSize: '16px',
  '&:hover': {
    backgroundColor: '#00235A'
  }
}));
export const TimerExpiredModal: React.FC<TimerExpiredModalProps> = ({
  open,
  onClose
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const theme = useTheme();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    selectedLocation,
    apptType,
    setErrors
  } = useLocationStore();
  const handleFindNewAppointment = () => {
    setErrors([]);
    if (pathname === '/') return onClose();
    const newPath = pathname.includes('/results') ? `/results/?fc=WN_${selectedLocation?.siteId}&appt-type=${apptType}` : '/results';
    router.push(newPath);
    onClose();
  };
  return <Dialog open={open}
  // Disable closing behaviors
  onClose={() => null} // Prevent closing on backdrop click
  disableEscapeKeyDown // Prevent closing with Escape key
  aria-labelledby="timer-expired-title" aria-describedby="timer-expired-description" maxWidth="sm" fullWidth data-sentry-element="Dialog" data-sentry-component="TimerExpiredModal" data-sentry-source-file="TimerExpiredModal.tsx">
			<Stack alignItems="center" p={3} data-sentry-element="Stack" data-sentry-source-file="TimerExpiredModal.tsx">
				<AccessTimeIcon fontSize="large" sx={{
        color: 'error.main',
        py: 3
      }} data-sentry-element="AccessTimeIcon" data-sentry-source-file="TimerExpiredModal.tsx" />
				<Typography variant={isExtraSmallScreen ? 'header4' : 'header3'} color="text.primary" id="timer-expired-title" data-sentry-element="Typography" data-sentry-source-file="TimerExpiredModal.tsx">
					Your Selected Time Is No Longer Available
				</Typography>
			</Stack>
			<DialogContent sx={{
      pb: 4
    }} data-sentry-element="DialogContent" data-sentry-source-file="TimerExpiredModal.tsx">
				<Stack alignItems="center" data-sentry-element="Stack" data-sentry-source-file="TimerExpiredModal.tsx">
					<Typography variant={isExtraSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} color="text.secondary" textAlign="center" id="timer-expired-description" sx={{
          mb: 2
        }} data-sentry-element="Typography" data-sentry-source-file="TimerExpiredModal.tsx">
						The time slot you selected has been released because the booking wasn&apos;t completed within
						the 7-minute hold period.
					</Typography>
					<Typography variant={isExtraSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} color="text.secondary" textAlign="center" data-sentry-element="Typography" data-sentry-source-file="TimerExpiredModal.tsx">
						Please select a new visit time to continue with your booking.
					</Typography>
				</Stack>
			</DialogContent>
			<DialogActions sx={{
      bgcolor: 'backgrounds.neutral',
      justifyContent: 'center',
      display: 'flex',
      p: 2
    }} data-sentry-element="DialogActions" data-sentry-source-file="TimerExpiredModal.tsx">
				<FindAppointmentButton onClick={handleFindNewAppointment} variant="contained" size={isExtraSmallScreen ? 'small' : 'medium'} fullWidth sx={{
        maxWidth: '280px',
        height: '48px'
      }} data-sentry-element="FindAppointmentButton" data-sentry-source-file="TimerExpiredModal.tsx">
					{pathname === '/' ? 'Dismiss' : 'Find a New Time'}
				</FindAppointmentButton>
			</DialogActions>
		</Dialog>;
};
export default TimerExpiredModal;